import { Box, CircularProgress, Typography } from '@mui/material';
import BackendService from 'BackendService';
import { debounce, DebouncedFunc } from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactDetails from '../../models/ContactDetails';
import { Palette } from 'Palette';
import { RADIUS } from 'DesignTokens';
import { StyledInput } from '../styles/StyledInputs';
import { StyledHeading } from '../styles/styles';

function getInitials(name: string): string {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

interface ToneProps {
  tone: keyof typeof Palette;
}

interface ContactSearchbarProps extends ToneProps {
  onResultClick: (id: string) => void;
}

interface State {
  query: string;
  results: ContactDetails[];
  loading: boolean;
  selectedIndex: number;
  showResults: boolean;
}

const ContactSearchBar = (props: ToneProps) => {
  const navigate = useNavigate();

  return (
    <ContactSearchBarImpl
      tone={props.tone}
      onResultClick={(id) => {
        navigate(`/contacts/${id}`);
      }}
    />
  );
};

class ContactSearchBarImpl extends React.Component<ContactSearchbarProps, State> {
  private searchRef = React.createRef<HTMLDivElement>();
  private inputRef = React.createRef<HTMLInputElement>();
  debouncedSearch: DebouncedFunc<(query: string) => void>;

  constructor(props: ContactSearchbarProps) {
    super(props);
    this.state = {
      query: '',
      results: [],
      loading: false,
      selectedIndex: -1,
      showResults: false,
    };

    this.debouncedSearch = debounce(this.performSearch.bind(this), 300);
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.searchRef.current && !this.searchRef.current.contains(event.target as Node)) {
      this.hideResults();
    }
  };

  hideResults = () => {
    this.setState({
      showResults: false,
      selectedIndex: -1,
    });
  };

  performSearch(query: string) {
    if (query.trim().length < 2) {
      this.setState({
        results: [],
        loading: false,
        showResults: false,
        selectedIndex: -1,
      });
      return;
    }

    this.setState({ loading: true });
    new BackendService().searchContact(query).then((results) => {
      this.setState({
        results,
        loading: false,
        showResults: true,
        selectedIndex: -1,
      });
    });
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    this.setState({ query });
    this.debouncedSearch(query);
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { results, selectedIndex, showResults, loading } = this.state;

    if (!showResults || results.length === 0 || loading) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.setState({
          selectedIndex: selectedIndex < results.length - 1 ? selectedIndex + 1 : 0,
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.setState({
          selectedIndex: selectedIndex > 0 ? selectedIndex - 1 : results.length - 1,
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          this.handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        this.hideResults();
        if (this.inputRef.current) {
          this.inputRef.current.blur();
        }
        break;
    }
  };

  handleResultClick = (contact: ContactDetails) => {
    this.setState(
      {
        results: [],
        query: '',
        showResults: false,
        selectedIndex: -1,
      },
      () => {
        this.props.onResultClick((contact as any).id);
      },
    );
  };

  handleClick = (id: string) => () => {
    this.props.onResultClick(id);
  };

  render() {
    const { query, results, loading, selectedIndex, showResults } = this.state;
    const tone = this.props.tone || 'default';

    return (
      <div ref={this.searchRef}>
        <StyledHeading tone={tone} fullTopSpacing sx={{ mb: 1.5 }}>
          Search Contacts
        </StyledHeading>
        <StyledInput
          ref={this.inputRef}
          fullWidth
          variant="outlined"
          placeholder="Search by name, email or mobile..."
          value={query}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          size="small"
        />

        {loading && <CircularProgress size={20} sx={{ mt: 1.5 }} />}

        {!loading && showResults && results.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
            {results.map((contact, idx) => (
              <Box
                key={(contact as any).id}
                component="button"
                type="button"
                onClick={() => this.handleResultClick(contact)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  textAlign: 'left',
                  px: 1.25,
                  py: 1,
                  backgroundColor:
                    idx === selectedIndex ? Palette.default.activeBg : Palette.tone1.background,
                  border: `1px solid ${Palette.tone1.border}`,
                  borderRadius: RADIUS.sectionChild,
                  cursor: 'pointer',
                  font: 'inherit',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': {
                    borderColor: Palette.default.buttonBg,
                    boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: Palette.default.activeBg,
                    color: Palette.default.activeText,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                  }}
                >
                  {getInitials(contact.name)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                    {contact.name}
                  </Typography>
                  <Typography noWrap variant="caption" color="text.secondary">
                    {contact.email || contact.mobile || 'No contact info'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {!loading && query.length >= 2 && results.length === 0 && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            No results found.
          </Typography>
        )}
      </div>
    );
  }
}

export default ContactSearchBar;
